var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Ecosystem, paraIdToName } from '../endpoints';
import { Asset } from '../types';
import { tickerPrice } from '../utils';
import { BN, bnToU8a, stringToU8a, u8aConcat } from '@polkadot/util';
export class Account32ValueBearing {
    constructor() {
        this.addressLength = 32;
    }
}
export class System extends Account32ValueBearing {
    constructor() {
        super();
        this.identifiers = ['system'];
    }
    extract(chain, account) {
        return __awaiter(this, void 0, void 0, function* () {
            const ticker = chain.api.registry.chainTokens[0];
            const price = yield tickerPrice(ticker);
            const accountData = yield chain.api.query.system.account(account);
            const decimals = new BN(chain.api.registry.chainDecimals[0]);
            const assets = [];
            if (!accountData.data.free.isZero()) {
                assets.push(new Asset({
                    name: `free ${ticker}`,
                    ticker,
                    price,
                    transferrable: true,
                    amount: accountData.data.free || new BN(0),
                    decimals,
                    origin: { account, chain: chain.name, source: 'system pallet' }
                }));
            }
            if (!accountData.data.reserved.isZero()) {
                assets.push(new Asset({
                    name: `reserved ${ticker}`,
                    ticker,
                    price,
                    transferrable: false,
                    amount: accountData.data.reserved || new BN(0),
                    decimals,
                    origin: { account, chain: chain.name, source: 'system pallet' }
                }));
            }
            return assets;
        });
    }
}
export class ParachainCrowdloan extends Account32ValueBearing {
    constructor() {
        super();
        this.identifiers = ['paras', 'crowdloan'];
    }
    extract(chain, account) {
        return __awaiter(this, void 0, void 0, function* () {
            const { api, name: chainName } = chain;
            // assumption: this pallet only lives on the relay chains.
            const ecosystem = chainName.toLowerCase() == 'polkadot' ? Ecosystem.Polkadot : Ecosystem.Kusama;
            const ticker = api.registry.chainTokens[0];
            const price = yield tickerPrice(ticker);
            const accountHex = api.createType('AccountId', account).toHex();
            const decimals = new BN(api.registry.chainDecimals[0]);
            const allParaIds = (yield api.query.paras.paraLifecycles.entries()).map(([key, _]) => key.args[0]);
            const assets = [];
            const fetchParaIdPromise = allParaIds.map((id) => __awaiter(this, void 0, void 0, function* () {
                const contribution = yield api.derive.crowdloan.ownContributions(id, [accountHex]);
                if (contribution[accountHex]) {
                    const contribution_amount = contribution[accountHex];
                    if (!contribution_amount.isZero()) {
                        const asset = new Asset({
                            name: `${paraIdToName(Number(id), ecosystem)} (${id}) crowdloan`,
                            ticker,
                            price,
                            transferrable: false,
                            amount: contribution_amount,
                            decimals,
                            origin: { account, chain: chainName, source: 'crowdloan pallet' }
                        });
                        assets.push(asset);
                    }
                }
            }));
            yield Promise.all(fetchParaIdPromise);
            return assets;
        });
    }
}
export class Assets extends Account32ValueBearing {
    constructor() {
        super();
        this.identifiers = ['assets'];
    }
    extract(chain, account) {
        return __awaiter(this, void 0, void 0, function* () {
            const { api, name: chainName } = chain;
            const assets = [];
            const allAssetIds = (yield api.query.assets.asset.entries()).map((a) => a[0].args[0]);
            const fetchAssetsPromise = allAssetIds.map((assetId) => __awaiter(this, void 0, void 0, function* () {
                const assetAccount = yield api.query.assets.account(assetId, account);
                if (assetAccount.isSome && !assetAccount.unwrap().balance.isZero()) {
                    const meta = yield api.query.assets.metadata(assetId);
                    const decimals = new BN(meta.decimals);
                    const ticker = (meta.symbol.toHuman() || '').toString();
                    const price = yield tickerPrice(ticker);
                    assets.push(new Asset({
                        ticker,
                        name: ticker,
                        price,
                        transferrable: Boolean(assetAccount.unwrap().isFrozen),
                        amount: assetAccount.unwrap().balance,
                        decimals,
                        origin: { account, chain: chainName, source: 'assets pallet' }
                    }));
                }
            }));
            yield Promise.all(fetchAssetsPromise);
            return assets;
        });
    }
}
export class NominationPools extends Account32ValueBearing {
    constructor() {
        super();
        this.identifiers = ['nominationPools'];
        this.rewardCounterBase = new BN(10).pow(new BN(18));
    }
    extract(chain, account) {
        return __awaiter(this, void 0, void 0, function* () {
            const { api, name: chainName } = chain;
            const assets = [];
            const maybeMember = yield api.query.nominationPools.poolMembers(account);
            if (maybeMember.isSome) {
                const ticker = chain.api.registry.chainTokens[0];
                const price = yield tickerPrice(ticker);
                const decimals = new BN(chain.api.registry.chainDecimals[0]);
                const member = maybeMember.unwrap();
                const bondedPool = (yield api.query.nominationPools.bondedPools(member.poolId)).unwrap();
                const pointsToBalance = (points) => __awaiter(this, void 0, void 0, function* () {
                    const bondedAccount = this.createAccount(api, api.consts.nominationPools.palletId.toU8a(), member.poolId, 0);
                    const bondedBalance = (yield api.query.staking.ledger(bondedAccount))
                        .unwrap()
                        .active.toBn();
                    return points.mul(bondedBalance).div(bondedPool.points);
                });
                // get the bonded amount
                const amount = yield pointsToBalance(member.points);
                assets.push(new Asset({
                    ticker,
                    name: `Bonded (nom-pools)`,
                    price,
                    transferrable: false,
                    amount,
                    decimals,
                    origin: { account, chain: chainName, source: 'nomination pools pallet' }
                }));
                // get any unbonding amount
                for (const [era, pointsUnbonding] of member.unbondingEras.entries()) {
                    const unbondingPool = (yield api.query.nominationPools.subPoolsStorage(member.poolId)).unwrap();
                    let amount = new BN(0);
                    if (unbondingPool.withEra.has(era)) {
                        const points = unbondingPool.withEra.get(era).points;
                        const balance = unbondingPool.withEra.get(era).balance;
                        amount = pointsUnbonding.mul(balance).div(points);
                    }
                    else {
                        const points = unbondingPool.noEra.points;
                        const balance = unbondingPool.noEra.balance;
                        amount = pointsUnbonding.mul(balance).div(points);
                    }
                    assets.push(new Asset({
                        ticker,
                        name: `Pool Unlocking @${era}`,
                        price,
                        transferrable: false,
                        amount,
                        decimals,
                        origin: { account, chain: chainName, source: 'nomination pools pallet' }
                    }));
                }
                // get any pending rewards
                const rewardPool = (yield api.query.nominationPools.rewardPools(member.poolId)).unwrap();
                const currentRewardCounter = yield this.currentRewardCounter(api, member.poolId, bondedPool.points, rewardPool);
                const pendingReward = currentRewardCounter
                    .sub(member.lastRecordedRewardCounter)
                    .mul(member.points)
                    .div(this.rewardCounterBase);
                if (!pendingReward.isZero()) {
                    assets.push(new Asset({
                        ticker,
                        name: `Pool Pending Reward`,
                        price,
                        transferrable: false,
                        amount: pendingReward,
                        decimals,
                        origin: { account, chain: chainName, source: 'nomination pools pallet' }
                    }));
                }
            }
            return assets;
        });
    }
    currentRewardCounter(api, id, bondedPoints, rewardPool) {
        return __awaiter(this, void 0, void 0, function* () {
            // https://github.com/paritytech/substrate/blob/4c83ee0a406939f1393d19f87675e1fbc49e328d/frame/nomination-pools/src/lib.rs#L968
            const rewardAccount = this.createAccount(api, api.consts.nominationPools.palletId.toU8a(), id, 1);
            const existentialDeposit = api.consts.balances.existentialDeposit;
            const balance = (yield api.query.system.account(rewardAccount)).data.free.sub(existentialDeposit);
            const payoutSinceLastRecord = balance
                .add(new BN(rewardPool.totalRewardsClaimed))
                .sub(new BN(rewardPool.lastRecordedTotalPayouts));
            return payoutSinceLastRecord
                .mul(this.rewardCounterBase)
                .div(bondedPoints)
                .add(rewardPool.lastRecordedRewardCounter);
        });
    }
    createAccount(api, palletId, poolId, index) {
        const EMPTY_H256 = new Uint8Array(32);
        const MOD_PREFIX = stringToU8a('modl');
        const U32_OPTS = { bitLength: 32, isLe: true };
        return api.registry.createType('AccountId32', u8aConcat(MOD_PREFIX, palletId, new Uint8Array([index]), bnToU8a(poolId, U32_OPTS), EMPTY_H256));
    }
}
